import { useState, Component, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { AdminSidebar } from "./AdminSidebar";
import { AdminTopBar } from "./AdminTopBar";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

type ErrorBoundaryState = { hasError: boolean; error: Error | null };

class AdminErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[AdminLayout] Uncaught error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="text-center max-w-md space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
              <p className="text-sm text-gray-500 mt-1">
                {this.state.error?.message ?? "An unexpected error occurred."}
              </p>
            </div>
            <Button
              onClick={() => this.setState({ hasError: false, error: null })}
              variant="outline"
              size="sm"
            >
              Try again
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = "sk_admin_sidebar_collapsed";

function readCollapsed(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
}
function writeCollapsed(value: boolean) {
  try { localStorage.setItem(STORAGE_KEY, String(value)); } catch { /* unavailable */ }
}

// ---------------------------------------------------------------------------
// AdminLayout — fully responsive
//
// Mobile  (< md)  : sidebar hidden, opens as a full-height Sheet drawer
//                   triggered by the hamburger button in AdminTopBar
// Tablet  (md+)   : sidebar always visible, collapsible to icon-only
// Desktop (lg+)   : same as tablet, default expanded
// ---------------------------------------------------------------------------

export function AdminLayout() {
  // Desktop collapse state (persisted)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed);

  // Mobile drawer state (not persisted — always starts closed)
  const [mobileOpen, setMobileOpen] = useState(false);

  function toggleSidebar() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });
  }

  return (
    <AdminErrorBoundary>
      <div className="flex h-screen overflow-hidden bg-gray-50">

        {/* ── Desktop sidebar (hidden on mobile) ─────────────────────── */}
        <div className="hidden md:flex">
          <AdminSidebar
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            onClose={() => setMobileOpen(false)}
          />
        </div>

        {/* ── Mobile drawer sidebar ───────────────────────────────────── */}
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
            {/* Sidebar panel */}
            <div className="fixed inset-y-0 left-0 z-50 w-72 md:hidden">
              <AdminSidebar
                collapsed={false}
                onToggle={toggleSidebar}
                onClose={() => setMobileOpen(false)}
                isMobileDrawer
              />
            </div>
          </>
        )}

        {/* ── Main content area ───────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <AdminTopBar
            onMenuClick={() => setMobileOpen((o) => !o)}
          />
          <main className="flex-1 overflow-y-auto bg-gray-50">
            <Outlet />
          </main>
        </div>

      </div>
    </AdminErrorBoundary>
  );
}
